import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useAuth } from '../../../../auth/AuthContext';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { surveyQuestionsApi } from '../../survey/surveyApi';
import type { SurveyQuestion } from '../../survey/surveyTypes';
import {
  ADDRESS_KIND,
  CITIZENSHIP_LABELS,
  CIVIL_STATUS_LABELS,
  DUAL_CITIZENSHIP_KIND_LABELS,
  EDUCATION_LEVEL_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  SEX_LABELS,
  type PersonnelAddress,
  type PersonnelDetail,
  type PersonnelEducation,
  type PersonnelEligibility,
  type PersonnelFamilyMember,
  type PersonnelRecognition,
  type PersonnelReference,
  type PersonnelSpecialSkill,
  type PersonnelSurveyAnswer,
  type PersonnelTraining,
  type PersonnelVoluntaryWork,
  type PersonnelEmploymentRecord,
} from '../personnelTypes';
import { usePersonIdentity } from '../usePersonIdentity';
import { usePersonNames, type PersonNames } from '../usePersonNames';
import { surveyFollowUps } from '../utils/surveyFollowUps';

/** Latest first, the form's own instruction for work and training ("start from the most recent"). */
function byPeriodDesc<T>(of: (row: T) => string | null | undefined) {
  return (a: T, b: T) => (of(b) ?? '').localeCompare(of(a) ?? '');
}

/**
 * The PERSONAL DATA SHEET — CS Form 212, read off the 201 file in the form's own order.
 *
 * **A VIEW, and it writes nothing.** Every value here is entered on the section that owns it,
 * which is why no card carries a cog: the cards rule lists "a DERIVED VIEW of fields another card
 * edits" among the cards that legitimately have no Edit. A second way to change a value would be
 * a second editor of it.
 *
 * **Only what the record holds.** The form also asks for place of birth, height, weight, a
 * telephone number and membership in associations; the 201 file has no column for any of them,
 * so they are not rows here — a row that can never be filled would state an absence the record
 * cannot correct.
 *
 * **Live rows only** — a deleted line is not part of what the employee declares.
 *
 * **The labels are the FORM's** (Surname, Name extension, Residential address, the numbered
 * section headings) where the sections say Last name, Suffix, Present — this page is the form
 * read back, and a reader holding the paper PDS beside it matches by the printed words. Columns
 * the form does not name keep the section panels' own headings. The 34–40 block keeps `Survey`,
 * because the form prints it with no heading of its own.
 *
 * Two values are NOT on this page and say so: an identification NUMBER (in `rsys_pii`, one
 * audited reveal per card on the Identification panel), and a child's date of birth (the
 * identity store again, one reveal per person). The name and the date of birth of the employee
 * ARE here, revealed on open for a reader holding `personnel.reveal`, exactly as Basic info does.
 */
export function PdsPanel({ record, erased }: { record: PersonnelDetail; erased: boolean }) {
  const { hasPermission } = useAuth();

  const revealable = hasPermission('personnel.reveal') && !erased;
  const { identity, failed } = usePersonIdentity(record.personId, revealable);

  const family = useMemo(
    () =>
      record.familyMembers
        .filter((m) => m.isActive)
        .sort((a, b) => a.relationSortOrder - b.relationSortOrder),
    [record.familyMembers],
  );

  const references = useMemo(() => record.references.filter((x) => x.isActive), [record.references]);

  /* ONE resolve for every person the sheet names — relatives and referees together. */
  const personIds = useMemo(
    () => [...family.map((m) => m.personId), ...references.map((x) => x.personId)],
    [family, references],
  );

  const names = usePersonNames(personIds);

  /*
    The employee's own name and date of birth. Three absences, as on Basic info: in flight,
    failed, and not given to this reader. `Hidden` is not an em dash — every employee has a name.
  */
  function identityValue(of: (found: NonNullable<typeof identity>) => ReactNode): ReactNode {
    if (identity) return of(identity);
    if (erased) return null;

    return (
      <span className="text-slate-400">
        {failed ? 'Unavailable' : revealable ? '…' : 'Hidden'}
      </span>
    );
  }

  const present = record.addresses.find((a) => a.kind === ADDRESS_KIND.present) ?? null;
  const permanent = record.addresses.find((a) => a.kind === ADDRESS_KIND.permanent) ?? null;

  return (
    <ProfileSection>
      <div className="flex flex-col gap-5">
        {erased ? (
          <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">
              This person&apos;s identity was erased. The name and date of birth cannot be recovered.
            </p>
          </div>
        ) : null}

        <ProfileCard id="personnel-pds-personal" title="I. Personal information">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Surname', value: identityValue((i) => i.lastName) },
              { label: 'First name', value: identityValue((i) => i.firstName) },
              { label: 'Name extension', value: identityValue((i) => i.suffix ?? null) },
              { label: 'Middle name', value: identityValue((i) => i.middleName ?? null) },
              {
                label: 'Date of birth',
                // A CALENDAR date — the conversion-free formatter.
                value: identityValue((i) => (i.dateOfBirth ? formatDateOnly(i.dateOfBirth) : null)),
                mono: true,
              },
              { label: 'Sex', value: SEX_LABELS[record.sex] },
              {
                label: 'Civil status',
                value: record.civilStatus ? CIVIL_STATUS_LABELS[record.civilStatus] : null,
              },
              { label: 'Blood type', value: record.bloodTypeName },
              /*
                One row per number type held. The NUMBER is in `rsys_pii` and reading it is an
                audited reveal, one card at a time, on the Identification panel — so this states
                that it is held, never an em dash, which would read as a number nobody recorded.
              */
              ...record.identifications.map((value) => ({
                label: value.typeName,
                value: <span className="text-slate-500">Number hidden</span>,
              })),
              { label: 'Agency employee no.', value: record.employeeNumber, mono: true },
              {
                label: 'Citizenship',
                value: record.citizenship ? CITIZENSHIP_LABELS[record.citizenship] : null,
              },
              {
                label: 'Dual citizenship',
                value: record.dualCitizenshipKind
                  ? DUAL_CITIZENSHIP_KIND_LABELS[record.dualCitizenshipKind]
                  : null,
              },
              { label: 'Country', value: record.dualCitizenshipCountryName ?? null },
              { label: 'Residential address', value: addressLine(present) },
              {
                label: 'Permanent address',
                // The flag is a statement, said as the Addresses panel says it — never the same
                // address printed a second time.
                value: record.permanentAddressSameAsPresent
                  ? 'Same as the residential address.'
                  : addressLine(permanent),
              },
              { label: 'Mobile no.', value: record.contactNumber ?? null, mono: true },
              { label: 'Email address', value: record.email ?? null, mono: true },
            ]}
          />
        </ProfileCard>

        <ProfileCard
          id="personnel-pds-family"
          title="II. Family background"
          count={family.length}
          padded
        >
          <PreviewGrid<PersonnelFamilyMember>
            rows={family}
            rowKey={(m) => m.id}
            empty="No family members recorded."
            columns={[
              { header: 'Relation', width: 130, render: (m) => <TextCell value={m.relationName} /> },
              { header: 'Name', width: 220, render: (m) => personCell(m.personId, names) },
              {
                header: 'Occupation',
                width: 160,
                render: (m) => <TextCell value={m.occupation ?? null} />,
              },
              {
                header: 'Employer / business name',
                width: 200,
                render: (m) => <TextCell value={m.employerName ?? null} />,
              },
              {
                header: 'Business address',
                width: 220,
                render: (m) => <TextCell value={m.businessAddress ?? null} />,
              },
              {
                header: 'Telephone no.',
                width: 140,
                render: (m) => <TextCell value={m.telephoneNumber ?? null} mono />,
              },
            ]}
          />
        </ProfileCard>

        <EducationCard rows={record.educations} />
        <EligibilityCard rows={record.eligibilities} />
        <WorkExperienceCard rows={record.employmentRecords} />
        <VoluntaryWorkCard rows={record.voluntaryWorks} />
        <TrainingCard rows={record.trainings} />
        <OtherInformationCard skills={record.specialSkills} recognitions={record.recognitions} />
        <QuestionsCard answers={record.surveyAnswers} />

        <ProfileCard
          id="personnel-pds-references"
          title="References"
          count={references.length}
          padded
        >
          <PreviewGrid<PersonnelReference>
            rows={references}
            rowKey={(x) => x.id}
            empty="No references recorded."
            columns={[
              { header: 'Name', width: 220, render: (x) => personCell(x.personId, names) },
              { header: 'Address', width: 280, render: (x) => <TextCell value={x.address ?? null} /> },
              {
                header: 'Contact number',
                width: 160,
                render: (x) => <TextCell value={x.contactNumber ?? null} mono />,
              },
            ]}
          />
        </ProfileCard>
      </div>
    </ProfileSection>
  );
}

/** The parts of one address on one line, in the form's order; null when nothing was recorded. */
function addressLine(address: PersonnelAddress | null): string | null {
  if (!address) return null;

  const parts = [
    address.streetAddress,
    address.barangay,
    address.cityMunicipality,
    address.province,
    address.zipCode,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

/** A relative's or a referee's name — the same three absences Family background draws. */
function personCell(personId: string, names: PersonNames) {
  if (names.failed) return <TextCell value="Unavailable" />;
  if (names.statusOf(personId) === 'erased') return <TextCell value="Erased" />;

  return <TextCell value={names.nameOf(personId) ?? (names.loading ? '…' : 'Unknown')} />;
}

function dateCell(value: string | null | undefined, open: string | null = null) {
  return <TextCell value={value ? formatDateOnly(value) : open} mono />;
}

function EducationCard({ rows }: { rows: PersonnelEducation[] }) {
  // The form lists the levels top to bottom, elementary first.
  const live = useMemo(
    () => rows.filter((x) => x.isActive).sort((a, b) => a.level - b.level),
    [rows],
  );

  return (
    <ProfileCard id="personnel-pds-education" title="III. Educational background" count={live.length} padded>
      <PreviewGrid<PersonnelEducation>
        rows={live}
        rowKey={(x) => x.id}
        empty="No education recorded."
        columns={[
          { header: 'Level', width: 150, render: (x) => <TextCell value={EDUCATION_LEVEL_LABELS[x.level]} /> },
          { header: 'School', width: 220, render: (x) => <TextCell value={x.schoolName} /> },
          {
            header: 'Degree or course',
            width: 200,
            render: (x) => <TextCell value={x.degreeOrCourse ?? null} />,
          },
          { header: 'From', width: 120, render: (x) => dateCell(x.periodFrom) },
          // Blank for study still in progress, as the Education panel draws it.
          { header: 'To', width: 120, render: (x) => dateCell(x.periodTo) },
          {
            header: 'Highest level / units',
            width: 160,
            render: (x) => <TextCell value={x.highestLevelUnitsEarned ?? null} />,
          },
          {
            // A year is an identifier — mono, never numeric.
            header: 'Graduated',
            width: 100,
            render: (x) => <TextCell value={x.yearGraduated != null ? String(x.yearGraduated) : null} mono />,
          },
          { header: 'Honours', width: 180, render: (x) => <TextCell value={x.honors ?? null} /> },
        ]}
      />
    </ProfileCard>
  );
}

function EligibilityCard({ rows }: { rows: PersonnelEligibility[] }) {
  const live = useMemo(() => rows.filter((x) => x.isActive), [rows]);

  return (
    <ProfileCard id="personnel-pds-eligibility" title="IV. Civil service eligibility" count={live.length} padded>
      <PreviewGrid<PersonnelEligibility>
        rows={live}
        rowKey={(x) => x.id}
        empty="No eligibilities recorded."
        columns={[
          { header: 'Eligibility', width: 240, render: (x) => <TextCell value={x.eligibilityName} /> },
          {
            header: 'Rating',
            width: 100,
            align: 'right',
            render: (x) => <TextCell value={x.rating != null ? x.rating.toFixed(2) : null} mono numeric />,
          },
          { header: 'Examined', width: 120, render: (x) => dateCell(x.dateOfExamination) },
          { header: 'Place', width: 200, render: (x) => <TextCell value={x.placeOfExamination ?? null} /> },
          { header: 'Licence no.', width: 140, render: (x) => <TextCell value={x.licenseNumber ?? null} mono /> },
          { header: 'Valid until', width: 120, render: (x) => dateCell(x.licenseValidUntil) },
        ]}
      />
    </ProfileCard>
  );
}

function WorkExperienceCard({ rows }: { rows: PersonnelEmploymentRecord[] }) {
  const live = useMemo(
    () => rows.filter((x) => x.isActive).sort(byPeriodDesc((x) => x.effectiveFrom)),
    [rows],
  );

  return (
    <ProfileCard id="personnel-pds-work-experience" title="V. Work experience" count={live.length} padded>
      <PreviewGrid<PersonnelEmploymentRecord>
        rows={live}
        rowKey={(x) => x.id}
        empty="No work experience recorded."
        /*
          Section V is EVERY line of Employment history — posts held here and previous employers
          (merged 27 Oct 2026). The heading stays the form's own words, because this panel is the
          form read back. A post here prints its UNIT in the employer column; exactly one of the
          two arrives, so the cell never prints a value twice.
        */
        columns={[
          { header: 'From', width: 120, render: (x) => dateCell(x.effectiveFrom) },
          // The form's own PRESENT, as the Employment history panel draws it.
          { header: 'To', width: 120, render: (x) => dateCell(x.effectiveTo, 'Present') },
          { header: 'Position title', width: 200, render: (x) => <TextCell value={x.positionTitle ?? null} /> },
          {
            header: 'Department / agency / company',
            width: 240,
            render: (x) => <TextCell value={x.orgUnitName ?? x.agencyName ?? null} />,
          },
          {
            header: 'Monthly salary',
            width: 140,
            align: 'right',
            render: (x) => <TextCell value={formatMoney(x.monthlySalary)} mono numeric />,
          },
          { header: 'SG', width: 70, render: (x) => <TextCell value={x.salaryGrade ?? null} mono /> },
          { header: 'Step', width: 70, render: (x) => <TextCell value={x.stepIncrement ?? null} mono /> },
          {
            header: 'Status of appointment',
            width: 170,
            render: (x) => (
              <TextCell
                value={x.appointmentStatus == null ? null : EMPLOYMENT_TYPE_LABELS[x.appointmentStatus]}
              />
            ),
          },
          {
            header: "Gov't service",
            width: 110,
            render: (x) => <TextCell value={x.isGovernmentService ? 'Yes' : 'No'} />,
          },
        ]}
      />
    </ProfileCard>
  );
}

function VoluntaryWorkCard({ rows }: { rows: PersonnelVoluntaryWork[] }) {
  const live = useMemo(
    () => rows.filter((x) => x.isActive).sort(byPeriodDesc((x) => x.periodFrom)),
    [rows],
  );

  return (
    <ProfileCard id="personnel-pds-voluntary-work" title="VI. Voluntary work" count={live.length} padded>
      <PreviewGrid<PersonnelVoluntaryWork>
        rows={live}
        rowKey={(x) => x.id}
        empty="No voluntary work recorded."
        columns={[
          { header: 'Organization', width: 220, render: (x) => <TextCell value={x.organizationName} /> },
          { header: 'Address', width: 220, render: (x) => <TextCell value={x.organizationAddress} /> },
          { header: 'From', width: 120, render: (x) => dateCell(x.periodFrom) },
          { header: 'To', width: 120, render: (x) => dateCell(x.periodTo, 'Present') },
          {
            header: 'Hours',
            width: 90,
            align: 'right',
            render: (x) => <TextCell value={x.numberOfHours != null ? String(x.numberOfHours) : null} mono numeric />,
          },
          {
            header: 'Position / nature of work',
            width: 200,
            render: (x) => <TextCell value={x.positionOrNature} />,
          },
        ]}
      />
    </ProfileCard>
  );
}

function TrainingCard({ rows }: { rows: PersonnelTraining[] }) {
  // Undated rows LAST — a missing date sorts as the empty string, which is the lowest.
  const live = useMemo(
    () => rows.filter((x) => x.isActive).sort(byPeriodDesc((x) => x.dateFrom)),
    [rows],
  );

  return (
    <ProfileCard id="personnel-pds-training" title="VII. Learning and development" count={live.length} padded>
      <PreviewGrid<PersonnelTraining>
        rows={live}
        rowKey={(x) => x.id}
        empty="No learning and development recorded."
        columns={[
          { header: 'Title', width: 240, render: (x) => <TextCell value={x.title} /> },
          { header: 'From', width: 120, render: (x) => dateCell(x.dateFrom) },
          // A null "To" is NOT "Present" for training — an em dash.
          { header: 'To', width: 120, render: (x) => dateCell(x.dateTo) },
          {
            header: 'Hours',
            width: 90,
            align: 'right',
            render: (x) => <TextCell value={x.numberOfHours != null ? String(x.numberOfHours) : null} mono numeric />,
          },
          // The CATEGORY is the form's "Type of LD" — personnel.md.
          { header: 'L&D category', width: 160, render: (x) => <TextCell value={x.categoryName} /> },
          {
            header: 'Conducted / sponsored by',
            width: 200,
            render: (x) => <TextCell value={x.sponsorName ?? null} />,
          },
        ]}
      />
    </ProfileCard>
  );
}

/** Section VIII — the two of its three columns the 201 file keeps. */
function OtherInformationCard({
  skills,
  recognitions,
}: {
  skills: PersonnelSpecialSkill[];
  recognitions: PersonnelRecognition[];
}) {
  const liveSkills = useMemo(() => skills.filter((x) => x.isActive), [skills]);
  const liveRecognitions = useMemo(() => recognitions.filter((x) => x.isActive), [recognitions]);

  return (
    <ProfileCard id="personnel-pds-other" title="VIII. Other information" padded>
      <div className="flex flex-col gap-4">
        <PreviewGrid<PersonnelSpecialSkill>
          title="Special skills/Hobbies"
          rows={liveSkills}
          rowKey={(x) => x.id}
          empty="No special skills recorded."
          columns={[
            { header: 'Skill', width: 240, render: (x) => <TextCell value={x.name} /> },
            { header: 'Category', width: 200, render: (x) => <TextCell value={x.categoryName} /> },
          ]}
        />

        <PreviewGrid<PersonnelRecognition>
          title="Recognitions"
          rows={liveRecognitions}
          rowKey={(x) => x.id}
          empty="No recognitions recorded."
          columns={[
            { header: 'Title', width: 240, render: (x) => <TextCell value={x.title} /> },
            { header: 'Awarded by', width: 200, render: (x) => <TextCell value={x.awardedBy ?? null} /> },
            { header: 'Date awarded', width: 130, render: (x) => dateCell(x.dateAwarded) },
          ]}
        />
      </div>
    </ProfileCard>
  );
}

/**
 * Items 34 to 40 — every question on the survey, answered or not, because the form asks all of
 * them. The Survey panel's own shape without its Update buttons.
 */
function QuestionsCard({ answers }: { answers: PersonnelSurveyAnswer[] }) {
  const [questions, setQuestions] = useState<SurveyQuestion[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);

    surveyQuestionsApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setQuestions(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the survey.', 0));
      });

    return () => controller.abort();
  }, [reloadKey]);

  const byQuestion = useMemo(() => {
    const map = new Map<string, PersonnelSurveyAnswer>();

    for (const answer of answers) {
      if (answer.isActive) map.set(answer.surveyQuestionId, answer);
    }

    return map;
  }, [answers]);

  const rows: PreviewRow[] = (questions ?? []).map((question) => {
    const answer = byQuestion.get(question.id) ?? null;

    return {
      label: question.number,
      value: (
        <span className="flex min-w-0 flex-col gap-1.5">
          <span className="text-slate-600">{question.text}</span>

          {answer ? (
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="font-medium text-slate-800">{answer.answer ? 'Yes' : 'No'}</span>

              {surveyFollowUps(answer).map((line) => (
                <span key={line.label} className="text-xs text-slate-500">
                  <span className="text-slate-400">{line.label}</span>{' '}
                  <span className={line.mono ? 'font-mono tabular-nums' : undefined}>{line.value}</span>
                </span>
              ))}
            </span>
          ) : (
            <span className="text-slate-400">Not answered</span>
          )}
        </span>
      ),
    };
  });

  return (
    <ProfileCard id="personnel-pds-survey" title="Survey" count={byQuestion.size}>
      {error ? (
        <div className="p-4">
          <ErrorState
            message={error.message}
            correlationId={error.correlationId}
            onRetry={() => setReloadKey((k) => k + 1)}
          />
        </div>
      ) : questions === null ? (
        <div className="p-4">
          <Skeleton className="h-20 w-full" />
        </div>
      ) : questions.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-500">
          No questions on the survey. The form&apos;s items are maintained under Personnel → Settings → Survey.
        </p>
      ) : (
        <PreviewTable variant="table-display" labelWidth="narrow" rows={rows} />
      )}
    </ProfileCard>
  );
}
